import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, CheckCircle2, CircleAlert, PackageCheck, Printer, QrCode, ScanLine, XCircle
} from 'lucide-react';
import { formatDateTime, formatMoney, formatQty } from '@erp/shared';
import { api, errorMessage, openPdf } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { PageHeader, DetailRow, ProgressBar, ErrorState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

interface KitLine {
  id: string; productId: string; sku: string; name: string; ruleType: string;
  requiredQty: number; theoreticalQty: number; scannedQty: number; actualQty: number;
  isMandatory: boolean; status: string; unitCode: string; unitKind: string; cost: number; pendingQty: number;
}

interface KitDetail {
  id: string; number: string; status: string; requiredCount: number; scannedCount: number;
  startedAt: string | null; completedAt: string | null; notes: string | null; createdAt: string;
  unitId: string; qrCode: string; unitStatus: string; widthMm: number; heightMm: number;
  configuration: Record<string, unknown>; serial: number; modelCode: string; modelName: string;
  orderNumber: string | null; orderId: string | null;
  lines: KitLine[];
  scans: { id: string; quantity: number; ean: string | null; createdAt: string; productId: string; isSubstitution: boolean; productSku: string; productName: string; voidedAt: string | null; voidReason: string | null; voidedByName: string | null }[];
  progress: number;
  canComplete: boolean;
  missingLines: number;
  trackingLabelUrl: string;
}

interface ScanResult {
  product: { id: string; sku: string; name: string };
  lineId: string;
  scannedQty: number;
  requiredQty: number;
  pendingQty: number;
  unitCode: string | null;
  complete: boolean;
  isSubstitution: boolean;
}

interface Feedback {
  tone: 'success' | 'warning' | 'error';
  title: string;
  message: string;
}

const FEEDBACK_STYLES: Record<Feedback['tone'], string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300',
  error: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300'
};

function beep(ok: boolean) {
  try {
    const AudioContextCtor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = ok ? 880 : 220;
    oscillator.type = 'sine';
    gain.gain.value = 0.06;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + (ok ? 0.08 : 0.25));
    setTimeout(() => void context.close(), 400);
  } catch { /* audio no disponible */ }
}

export function KitDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { can } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [qty, setQty] = useState('1');
  const [last, setLast] = useState<Feedback | null>(null);
  const [justLine, setJustLine] = useState<string | null>(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [labelReady, setLabelReady] = useState(false);
  // Anulación de escaneos: qué escaneo se está anulando y el motivo (obligatorio, va a auditoría).
  const [voidTarget, setVoidTarget] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState<string | null>(null);

  const kitQuery = useQuery({
    queryKey: ['kit', id],
    queryFn: () => api<KitDetail>(`/kits/${id}`),
    enabled: Boolean(id)
  });

  const kit = kitQuery.data;

  useEffect(() => {
    if (kit && kit.status !== 'completed') inputRef.current?.focus();
  }, [kit?.status, kit?.id]);

  useEffect(() => {
    if (!justLine) return;
    const timer = window.setTimeout(() => setJustLine(null), 2600);
    return () => window.clearTimeout(timer);
  }, [justLine]);

  const scan = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim() || !kit) return;
    try {
      const result = await api<ScanResult>(`/kits/${kit.id}/scan`, {
        method: 'POST',
        body: { ean: code.trim(), quantity: Number(qty) || 1 }
      });
      const unit = result.unitCode ?? '';
      const substitution = result.isSubstitution ? ' · sustitución autorizada' : '';
      setLast({
        tone: result.complete ? 'success' : 'warning',
        title: result.complete ? 'Sin faltantes' : 'Añadido, todavía faltan unidades',
        message: result.complete
          ? `${result.product.sku} · ${formatQty(result.scannedQty)} / ${formatQty(result.requiredQty)} ${unit}${substitution}`
          : `${result.product.sku} · faltan ${formatQty(result.pendingQty)} ${unit}${substitution}`
      });
      beep(result.complete);
      setJustLine(result.lineId);
      setCode('');
      document.getElementById(`kit-line-${result.lineId}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      await qc.invalidateQueries({ queryKey: ['kit', id] });
      await qc.invalidateQueries({ queryKey: ['kits'] });
      await qc.invalidateQueries({ queryKey: ['kit-stats'] });
    } catch (error) {
      beep(false);
      setLast({ tone: 'error', title: 'No se pudo añadir', message: errorMessage(error) });
    } finally {
      inputRef.current?.focus();
    }
  };

  /** Anula un escaneo con un motivo obligatorio: queda en el historial y en la auditoría. */
  const voidScan = async (scanId: string) => {
    if (!kit || !voidReason.trim()) return;
    setVoiding(scanId);
    try {
      await api(`/kits/${kit.id}/scans/${scanId}/void`, {
        method: 'POST',
        body: { reason: voidReason.trim() }
      });
      toast.success('Escaneo anulado');
      setVoidReason('');
      setVoidTarget(null);
      await qc.invalidateQueries({ queryKey: ['kit', id] });
      await qc.invalidateQueries({ queryKey: ['kits'] });
      await qc.invalidateQueries({ queryKey: ['kit-stats'] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setVoiding(null);
    }
  };

  const complete = async () => {
    if (!kit) return;
    setCompleting(true);
    try {
      const result = await api<{ totalCost: number; trackingLabelUrl: string; message: string }>(`/kits/${kit.id}/complete`, { method: 'POST' });
      toast.success(result.message);
      setCompleteOpen(false);
      setLabelReady(true);
      await qc.invalidateQueries({ queryKey: ['kit', id] });
      await qc.invalidateQueries({ queryKey: ['kits'] });
      await qc.invalidateQueries({ queryKey: ['kit-stats'] });
      await qc.invalidateQueries({ queryKey: ['stock'] });
      // La etiqueta de seguimiento se emite al cerrar el kit: se abre directamente
      // para que el operario la imprima y la adhiera a la unidad.
      try {
        await openPdf(result.trackingLabelUrl);
      } catch {
        toast.error('El kit se cerró, pero no se pudo abrir la etiqueta. Usa el botón «Etiqueta de seguimiento».');
      }
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCompleting(false);
    }
  };

  if (kitQuery.error) return <ErrorState message={(kitQuery.error as Error).message} onRetry={() => void kitQuery.refetch()} />;
  if (kitQuery.isLoading || !kit) return <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-28 animate-pulse rounded-lg bg-muted" />)}</div>;

  const workable = kit.status !== 'completed' && kit.status !== 'cancelled';
  const scanPermission = can('kits.scan') && workable;
  const completePermission = can('kits.complete') && workable;
  const missing = kit.lines.filter((line) => line.pendingQty > 0.0005);
  const completeLines = kit.lines.length - missing.length;
  const isCompleted = kit.status === 'completed';
  const labelUrl = kit.trackingLabelUrl ?? `/kits/${kit.id}/tracking-label.pdf`;
  const scanTone = last?.tone === 'success'
    ? 'border-emerald-500 ring-4 ring-emerald-500/10'
    : last?.tone === 'warning'
      ? 'border-amber-500 ring-4 ring-amber-500/10'
      : last?.tone === 'error'
        ? 'border-red-500 ring-4 ring-red-500/10'
        : '';

  return (
    <div>
      <div className="mb-3">
        <Link to="/kits" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><ArrowLeft className="size-3" /> Kits a preparar</Link>
      </div>
      <PageHeader
        title={kit.number}
        badge={<StatusBadge status={kit.status} />}
        description={`${kit.modelName} · ${kit.widthMm}×${kit.heightMm} mm · ${kit.orderNumber ?? 'Sin pedido'}`}
        actions={(
          <>
            <Button variant="outline" size="sm" onClick={() => void openPdf(`/mirror-units/${kit.unitId}/label.pdf`)}><Printer /> QR unidad</Button>
            <Button variant="ghost" size="sm" title="Etiqueta de la unidad en ZPL (impresora térmica)"
              onClick={() => window.open(`/api/mirror-units/${kit.unitId}/label.zpl`, '_blank', 'noopener')}>ZPL unidad</Button>
            <Button variant={isCompleted ? 'default' : 'outline'} size="sm" onClick={() => void openPdf(labelUrl)}>
              <Printer /> Etiqueta de seguimiento
            </Button>
            <Button variant="ghost" size="sm" title="Etiqueta de seguimiento del kit en ZPL (impresora térmica)"
              onClick={() => window.open(`/api/kits/${kit.id}/tracking-label.zpl`, '_blank', 'noopener')}>ZPL kit</Button>
            {completePermission ? (
              <Button size="sm" variant={kit.canComplete ? 'success' : 'default'} disabled={!kit.canComplete} onClick={() => setCompleteOpen(true)}>
                <PackageCheck /> Finalizar kit
              </Button>
            ) : null}
          </>
        )}
      />

      <div className="grid gap-4 xl:grid-cols-[1fr_340px]">
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="Escaneo de componentes"
              description={isCompleted ? 'El kit está cerrado; no admite más escaneos.' : 'Escanea el EAN o SKU. La línea se pone en verde cuando no falta ninguna unidad.'}
              actions={<Badge tone={isCompleted || missing.length === 0 ? 'success' : 'warning'}>{isCompleted ? 'Kit cerrado' : missing.length === 0 ? 'Kit completo' : `Faltan ${missing.length} líneas`}</Badge>}
            />
            <CardContent>
              {isCompleted ? (
                <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-xs text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                  <CheckCircle2 className="size-4 shrink-0" />
                  Preparación finalizada. La etiqueta de seguimiento está disponible en la cabecera y en el panel lateral.
                </div>
              ) : (
              <form onSubmit={scan} className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <ScanLine className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-primary" />
                  <input
                    ref={inputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Escanear EAN o SKU…"
                    className={cn('scan-input pl-11', scanTone)}
                    disabled={!scanPermission}
                    autoComplete="off"
                  />
                </div>
                <div className="flex gap-2">
                  <Input type="number" step="0.001" min="0.001" value={qty} onChange={(e) => setQty(e.target.value)} className="h-14 w-24 text-center text-base" title="Cantidad" disabled={!scanPermission} />
                  <Button type="submit" className="h-14 px-5" disabled={!scanPermission || !code.trim()}>Añadir</Button>
                </div>
              </form>
              )}
              {isCompleted ? null : last ? (
                <div className={cn('mt-3 flex items-start gap-2 rounded-md border px-3 py-2 text-xs', FEEDBACK_STYLES[last.tone])}>
                  {last.tone === 'success' ? <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
                    : last.tone === 'warning' ? <CircleAlert className="mt-0.5 size-4 shrink-0" />
                      : <XCircle className="mt-0.5 size-4 shrink-0" />}
                  <span><strong className="font-semibold">{last.title}.</strong> {last.message}</span>
                </div>
              ) : (
                <p className="mt-3 text-[11px] text-muted-foreground">El escáner debe estar configurado en modo teclado (Enter al final). Los metros y m² admiten decimales.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title="Componentes del kit"
              description={`${completeLines} de ${kit.lines.length} líneas completas · ${kit.scans.filter((s) => !s.voidedAt).length} escaneos${kit.scans.some((s) => s.voidedAt) ? ` (${kit.scans.filter((s) => s.voidedAt).length} anulados)` : ''}`}
              actions={<Badge tone={isCompleted || kit.canComplete ? 'success' : 'warning'}>{isCompleted ? 'Kit cerrado' : kit.canComplete ? 'Listo para cerrar' : 'Faltan componentes'}</Badge>}
            />
            <div className="overflow-x-auto">
              <table className="table-dense w-full min-w-[760px]">
                <thead className="border-b border-border bg-muted/30">
                  <tr><th>Componente</th><th className="text-right">Teórico</th><th className="text-right">Requerido</th><th className="text-right">Escaneado</th><th className="text-right">Pendiente</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {kit.lines.map((line) => {
                    const lineComplete = line.pendingQty <= 0.0005;
                    const partial = !lineComplete && line.scannedQty > 0;
                    return (
                      <tr
                        id={`kit-line-${line.id}`}
                        key={line.id}
                        className={cn(
                          'border-l-4',
                          lineComplete
                            ? 'border-l-emerald-500 bg-emerald-50/70 dark:bg-emerald-500/10'
                            : partial
                              ? 'border-l-amber-400 bg-amber-50/50 dark:bg-amber-500/10'
                              : 'border-l-transparent',
                          justLine === line.id && 'ring-2 ring-inset ring-emerald-400'
                        )}
                      >
                        <td>
                          <p className="font-medium">{line.name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{line.sku} · {line.unitCode}</p>
                        </td>
                        <td className="text-right tabular-nums text-muted-foreground">{formatQty(line.theoreticalQty)}</td>
                        <td className="text-right tabular-nums">{formatQty(line.requiredQty)}</td>
                        <td className={cn('text-right font-medium tabular-nums', lineComplete && 'text-emerald-700 dark:text-emerald-400')}>{formatQty(line.scannedQty)}</td>
                        <td className="text-right tabular-nums">
                          {lineComplete
                            ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="size-4" /> 0</span>
                            : <span className="font-medium text-amber-600">{formatQty(line.pendingQty)}</span>}
                        </td>
                        <td>
                          {lineComplete
                            ? <Badge tone="success"><CheckCircle2 /> Completo</Badge>
                            : partial ? <Badge tone="warning">Incompleto</Badge> : <Badge tone="neutral">Pendiente</Badge>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>

          <Card>
            <CardHeader title="Historial de escaneos" description="Trazabilidad de usuario, terminal y hora, con las anulaciones a la vista" />
            <div className="max-h-56 overflow-y-auto">
              <table className="table-dense w-full">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur"><tr><th>Hora</th><th>Material</th><th className="text-right">Cantidad</th><th /></tr></thead>
                <tbody>
                  {kit.scans.length === 0 ? <tr><td colSpan={4} className="py-6 text-center text-xs text-muted-foreground">Sin escaneos</td></tr> : [...kit.scans].reverse().map((item) => (
                    <tr key={item.id} className={item.voidedAt ? 'opacity-60' : undefined} data-voided={item.voidedAt ? '1' : undefined}>
                      <td className="text-[10px] text-muted-foreground">{new Date(item.createdAt).toLocaleTimeString('es-ES')}</td>
                      <td>
                        <span className={cn('font-mono text-[10px]', item.voidedAt && 'line-through')}>{item.productSku}</span>
                        {item.isSubstitution ? <Badge tone="warning">Sustitución</Badge> : null}
                        {item.voidedAt ? <Badge tone="neutral">Anulado</Badge> : null}
                        {item.voidedAt ? (
                          <span className="ml-1 text-[10px] text-muted-foreground">
                            {item.voidedByName ?? '—'} · {item.voidReason ?? 'sin motivo'}
                          </span>
                        ) : null}
                      </td>
                      <td className={cn('text-right tabular-nums', item.voidedAt && 'line-through')}>{formatQty(item.quantity)}</td>
                      <td className="text-right">
                        {scanPermission && !item.voidedAt ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setVoidTarget(item.id); setVoidReason(''); }}
                          >
                            Anular
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {voidTarget ? (
                <div className="flex flex-col gap-2 border-t border-border p-3" data-void-form>
                  <label className="text-[11px] font-medium" htmlFor="void-reason">Motivo de la anulación (queda en la auditoría)</label>
                  <div className="flex gap-2">
                    <Input
                      id="void-reason"
                      value={voidReason}
                      onChange={(event) => setVoidReason(event.target.value)}
                      placeholder="Doble disparo del lector, pieza equivocada…"
                      maxLength={200}
                    />
                    <Button
                      size="sm"
                      disabled={voidReason.trim().length < 3 || voiding !== null}
                      onClick={() => void voidScan(voidTarget)}
                    >
                      {voiding ? 'Anulando…' : 'Confirmar'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setVoidTarget(null)}>Cancelar</Button>
                  </div>
                </div>
              ) : null}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardContent className="flex flex-col items-center pt-5">
              <div className="rounded-lg border border-border bg-white p-2">
                <img src={`/api/mirror-units/${kit.unitId}/qr.png`} alt={kit.qrCode} className="size-40" />
              </div>
              <p className="mt-3 font-mono text-sm font-semibold">{kit.qrCode}</p>
              <p className="text-[11px] text-muted-foreground">QR interno de fabricación</p>
              <div className="mt-4 w-full">
                <div className="mb-1 flex justify-between text-[11px]"><span className="text-muted-foreground">Progreso</span><span className="font-medium">{kit.progress}%</span></div>
                <ProgressBar value={kit.progress} tone={kit.canComplete ? 'success' : 'primary'} />
              </div>
            </CardContent>
          </Card>

          {labelReady || isCompleted ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="size-4" /> Etiqueta de seguimiento emitida</p>
              <p className="mt-1">Se generó con el QR y el código de barras del kit. Adhiérela a la unidad antes de pasarla a producción.</p>
              <Button variant="success" size="sm" className="mt-3 w-full" onClick={() => void openPdf(labelUrl)}><Printer /> Imprimir etiqueta</Button>
            </div>
          ) : null}

          {completeOpen ? (
            <Card>
              <CardHeader title="Finalizar kit y emitir etiqueta" />
              <CardContent className="space-y-3 text-xs">
                <p className="text-muted-foreground">Se descontará el consumo real de cada componente, se liberarán las reservas y se emitirá la etiqueta de seguimiento del kit.</p>
                <p>Kit <span className="font-mono font-medium">{kit.number}</span> · unidad <span className="font-mono font-medium">{kit.qrCode}</span>.</p>
                <p>Coste real estimado: <span className="font-semibold">{formatMoney(kit.lines.reduce((acc, line) => acc + line.actualQty * line.cost, 0))}</span>.</p>
                {missing.length > 0 ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                    <p className="font-medium">Faltan {missing.length} líneas obligatorias:</p>
                    <ul className="mt-1 space-y-0.5">
                      {missing.slice(0, 5).map((line) => <li key={line.id} className="font-mono text-[10px]">{line.sku} · {formatQty(line.pendingQty)} {line.unitCode}</li>)}
                    </ul>
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => setCompleteOpen(false)}>Cancelar</Button>
                  <Button variant="success" size="sm" className="flex-1" loading={completing} disabled={!kit.canComplete} onClick={() => void complete()}>Confirmar y emitir</Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader title="Unidad" />
            <CardContent>
              <DetailRow label="Modelo" value={`${kit.modelName} (${kit.modelCode})`} />
              <DetailRow label="Medidas" value={`${kit.widthMm} × ${kit.heightMm} mm`} />
              <DetailRow label="Serie" value={kit.serial} />
              <DetailRow label="Estado unidad" value={<StatusBadge status={kit.unitStatus} />} />
              <DetailRow label="Pedido" value={kit.orderNumber ?? '—'} />
              <DetailRow label="Creado" value={new Date(kit.createdAt).toLocaleString('es-ES')} />
              {kit.startedAt ? <DetailRow label="Iniciado" value={new Date(kit.startedAt).toLocaleString('es-ES')} /> : null}
              {kit.completedAt ? <DetailRow label="Completado" value={formatDateTime(kit.completedAt)} /> : null}
            </CardContent>
          </Card>

          {isCompleted ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-800 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="size-4" /> Kit cerrado</p>
              <p className="mt-1">El stock real se ha descontado, la unidad está en producción y la etiqueta de seguimiento está disponible.</p>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
              <p className="flex items-center gap-2 font-semibold"><CircleAlert className="size-4" /> Cierre controlado</p>
              <p className="mt-1">No se permite cerrar si falta algún componente obligatorio. Las sustituciones solo se aceptan si están autorizadas en la BOM.</p>
            </div>
          )}

          <div className="flex gap-2">
            <Link to="/packaging" className="flex-1"><Button variant="outline" size="sm" className="w-full"><QrCode /> Ir a embalaje</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
